import { appConfig } from "@/config";
import { HttpMethod } from "@/types";
import {
  ActiveAutoAnnotationBatchResponse,
  CreateAutoAnnotationBatch,
  CreateAutoAnnotationBatchResponse,
  RunVisionRequest,
  TaskDetail,
  TaskHistory,
  UpdateTask,
  VisionProjectNote,
  VisionRunResponse,
  VisionTranscriptResponse,
} from "@repo/schema";
import { captureApi, CaptureApiTagType } from "@/modules/capture/services/captureApi";

const { tasks, predict } = appConfig.studioApi.endpoints;

/**
 * A finished run changes durable state now: its boxes are staged server-side.
 *
 * Invalidating the list is what lights the awaiting-review chip up on a row
 * the labeller has already walked away from — switching tasks does not unmount
 * the label page, so the mutation still settles and the sidebar catches up on
 * its own.
 */
const invalidateAfterRun = (
  _result: unknown,
  _error: unknown,
  { projectId, taskId, body }: { projectId: number; taskId: number; body: RunVisionRequest },
) => {
  const batchId = body.batchId;
  return [
    // A batch run deliberately does NOT invalidate the list.
    //
    // The fan-out is unbounded, so forty images finishing means forty full
    // task-list refetches landing on top of each other — for a list whose
    // rows are already being driven by the batch's own poll. The list is
    // refetched once, by the batch hook, when the run settles.
    ...(batchId == null ? [{ type: CaptureApiTagType.Tasks, id: projectId }] : []),
    { type: CaptureApiTagType.Tasks, id: `detail-${taskId}` },
  ];
};

export const labelApi = captureApi.injectEndpoints({
  endpoints: (builder) => ({
    getTask: builder.query<TaskDetail, { projectId: number; taskId: number }>({
      query: ({ projectId, taskId }) => ({
        url: tasks.task(projectId, taskId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { taskId }) => [
        { type: CaptureApiTagType.Tasks, id: `detail-${taskId}` },
      ],
    }),
    getTaskHistory: builder.query<TaskHistory, { projectId: number; taskId: number }>({
      query: ({ projectId, taskId }) => ({
        url: tasks.history(projectId, taskId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { taskId }) => [
        { type: CaptureApiTagType.Tasks, id: `history-${taskId}` },
      ],
    }),
    updateTask: builder.mutation<
      TaskDetail,
      { projectId: number; taskId: number; body: UpdateTask }
    >({
      query: ({ projectId, taskId, body }) => ({
        url: tasks.task(projectId, taskId),
        method: HttpMethod.PUT,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId, taskId }) => [
        { type: CaptureApiTagType.Tasks, id: projectId },
        { type: CaptureApiTagType.Tasks, id: `detail-${taskId}` },
        { type: CaptureApiTagType.Tasks, id: `history-${taskId}` },
      ],
    }),
    /**
     * Robopipe Vision, turn 1: one synchronous Vision model call that opens a
     * conversation for this image. Slow by nature (tens of seconds), so the
     * caller holds the RTK handle to `.abort()` it.
     */
    runVisionDetection: builder.mutation<
      VisionRunResponse,
      { projectId: number; taskId: number; body: RunVisionRequest }
    >({
      query: ({ projectId, taskId, body }) => ({
        url: predict.vision(projectId, taskId),
        method: HttpMethod.POST,
        body,
      }),
      invalidatesTags: invalidateAfterRun,
    }),
    /**
     * The turn ledger of a conversation the labeler is resuming.
     *
     * Lazy on purpose: whether a conversation exists rides on the task detail
     * the label page already fetches for every image, and this — the part that
     * grows with the conversation — is only paid for when somebody actually
     * opens it. Untagged and refetched after each turn rather than
     * cache-invalidated: there is exactly one consumer, and it knows precisely
     * when a new turn landed.
     */
    getVisionTranscript: builder.query<
      VisionTranscriptResponse,
      { projectId: number; taskId: number; conversationId: number }
    >({
      query: ({ projectId, taskId, conversationId }) => ({
        url: predict.visionConversation(projectId, taskId, conversationId),
        method: HttpMethod.GET,
      }),
    }),
    /**
     * Save a feedback turn as a project note when the model judged it specific
     * to its image. The server copies the text from the stored turn and makes
     * the caller the author. Untagged: the one caller knows what changed and
     * refreshes the settings list itself, as it does after a feedback turn.
     */
    saveVisionTurnNote: builder.mutation<
      VisionProjectNote,
      { projectId: number; taskId: number; conversationId: number; turn: number }
    >({
      query: ({ projectId, taskId, conversationId, turn }) => ({
        url: predict.visionTurnNote(projectId, taskId, conversationId, turn),
        method: HttpMethod.POST,
      }),
    }),
    /**
     * Open a batch over the operator's selection.
     *
     * The API filters the selection (already-annotated and already-staged
     * images are dropped and reported as counts) and hands back the batch with
     * one PENDING item per accepted image. Dispatch is still the browser's
     * job: this only creates the record the runs report into.
     */
    createAutoAnnotateBatch: builder.mutation<
      CreateAutoAnnotationBatchResponse,
      { projectId: number; body: CreateAutoAnnotationBatch }
    >({
      query: ({ projectId, body }) => ({
        url: predict.autoAnnotateBatch(projectId),
        method: HttpMethod.POST,
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: CaptureApiTagType.Tasks, id: `batch-${projectId}` },
      ],
    }),
    /**
     * The live batch, polled while one is running.
     *
     * Also the only thing that closes an abandoned batch — the API evaluates
     * liveness on read, because nothing in it runs on a timer. Tagged so
     * creating a batch refetches immediately rather than waiting out a poll
     * interval.
     */
    getActiveAutoAnnotateBatch: builder.query<
      ActiveAutoAnnotationBatchResponse,
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: predict.activeAutoAnnotateBatch(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: CaptureApiTagType.Tasks, id: `batch-${projectId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTaskQuery,
  useGetTaskHistoryQuery,
  useUpdateTaskMutation,
  useRunVisionDetectionMutation,
  useSaveVisionTurnNoteMutation,
  useLazyGetVisionTranscriptQuery,
  useCreateAutoAnnotateBatchMutation,
  useGetActiveAutoAnnotateBatchQuery,
} = labelApi;
